<template>
  <div>
    <v-container fluid class="px-6 py-4">
      <v-row align="center" class="mb-4">
        <v-col cols="12">
          <v-card
            elevation="0"
            class="pa-1 d-flex align-center white rounded-lg border-subtle"
          >
            <div class="d-flex align-center">
              <v-avatar color="primary lighten-5" size="56" class="mr-4 rounded-lg">
                <v-icon color="primary" large>mdi-truck-fast</v-icon>
              </v-avatar>
              <div>
                <h1 class="text-h5 font-weight-bold mb-1 primary--text text--darken-3">Delivery Listing</h1>
                <div class="subtitle-1 grey--text text--darken-2 font-weight-medium">
                  Manage shipments, track status, and view delivery history (Max 12 months look-back)
                </div>
              </div>
            </div>
            <v-spacer></v-spacer>
            <div class="text-right hidden-sm-and-down px-6 py-2 grey lighten-5 rounded-lg border-subtle">
              <div class="overline grey--text text--darken-1 font-weight-bold" style="letter-spacing: 1px;">Total Records</div>
              <div class="text-h4 font-weight-black primary--text">{{ serverItemsLength.toLocaleString() }}</div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-expansion-panels v-model="filterPanelExpanded" flat class="rounded-lg mb-6">
        <div :class="{ 'loading-disabled': loading }" style="width: 100%">
          <v-expansion-panel class="border-subtle rounded-lg">
            <v-expansion-panel-header class="grey lighten-5 py-3">
              <div class="d-flex align-center">
                <v-icon left color="grey darken-1">mdi-filter-variant</v-icon>
                <span class="subtitle-2 font-weight-bold grey--text text--darken-3 text-uppercase mr-3">Filters</span>

                <v-fade-transition>
                  <span v-if="appliedFilters.length" class="d-flex flex-wrap align-center">
                    <v-divider vertical class="mx-3 hidden-xs-only"></v-divider>
                    <v-chip
                      v-for="(filter, i) in appliedFilters"
                      :key="i"
                      :close="filter.key !== 'source' && filter.key !== 'deleted' && !(filter.key === 'country' && !isInternalUser)"
                      @click:close="filter.key !== 'source' && filter.key !== 'deleted' && !(filter.key === 'country' && !isInternalUser) && removeFilter(filter.key)"
                      small
                      label
                      color="primary"
                      class="mr-1 my-1 font-weight-medium px-2"
                      style="border: 1px solid rgba(25, 118, 210, 0.2); background-color: rgba(25, 118, 210, 0.08) !important;"
                      text-color="primary darken-2"
                    >
                      <span class="grey--text text--darken-2 mr-1">{{ filter.label }}:</span>
                      <strong>{{ filter.value }}</strong>
                    </v-chip>
                    <v-btn
                      v-if="canClearFilters"
                      medium
                      text
                      color="error"
                      @click.stop="resetFilters"
                      class="ml-2 text-capitalize caption font-weight-bold"
                    >
                      Reset All
                    </v-btn>
                  </span>
                </v-fade-transition>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="white pt-4">
              <v-progress-linear
                :active="loading"
                indeterminate
                absolute
                top
                height="3"
                color="primary"
              ></v-progress-linear>

              <v-row dense>
                <v-col cols="12" md="3">
                  <div class="filter-group-wrapper h-100">
                    <div class="filter-category-tag mb-2">
                      <span class="caption font-weight-bold blue--text text--darken-3 text-uppercase d-flex align-center">
                        <v-icon x-small left color="blue darken-3">fas fa-globe-asia</v-icon>
                        Scope & Territory
                      </span>
                    </div>
                    <v-select class="slim-input mb-2" label="Source" v-model="filters.source" :items="sourceOptions" dense outlined hide-details :clearable="false" background-color="white" />
                    <!-- <v-select class="slim-input mb-2" label="Deleted" v-model="filters.deleted" :items="deletedOptions" dense outlined hide-details :clearable="false" background-color="white" /> -->
                    <!-- <v-autocomplete v-if="isInternalUser" class="slim-input mb-2" label="Country" v-model="filters.country" :items="countryOptions" item-text="text" item-value="value" clearable dense outlined hide-details background-color="white"/> -->
                    <v-autocomplete v-if="isInternalUser" class="slim-input mb-2" label="Client" v-model="filters.client" :items="filteredClients" :loading="clientsLoading" :search-input.sync="clientSearch" item-text="text" item-value="value" multiple small-chips deletable-chips clearable dense outlined hide-details background-color="white"/>
                    <v-autocomplete class="slim-input mb-2" label="Region" v-model="filters.region" :items="filteredRegions.map(function(r) { return { text: r.id + ' - ' + r.name, value: r.id }; })" :loading="regionsLoading" :search-input.sync="regionSearch" clearable dense outlined hide-details background-color="white"/>
                    <v-autocomplete class="slim-input mb-2" label="Depot" v-model="filters.depot" :items="filteredDepots.map(function(d) { return { text: d.id + ' - ' + d.name, value: d.id }; })" :loading="depotsLoading" :search-input.sync="depotSearch" clearable dense outlined hide-details background-color="white"/>
                    <!-- <v-autocomplete class="slim-input" label="Consol. Depot" v-model="filters.consolidationDepot" :items="filteredConsolidationDepots" :loading="depotsLoading" :search-input.sync="consolidationDepotSearch" item-text="text" item-value="value" clearable dense outlined hide-details background-color="white"/> -->
                  </div>
                </v-col>

                <!-- Client & Contact filters hidden for now
                <v-col cols="12" md="3">
                  <div class="filter-group-wrapper h-100">
                    <div class="filter-category-tag mb-2">
                      <span class="caption font-weight-bold orange--text text--darken-3 text-uppercase d-flex align-center">
                        <v-icon x-small left color="orange darken-3">fas fa-user-friends</v-icon>
                        Client & Contact
                      </span>
                    </div>
                    <v-autocomplete v-if="isInternalUser" class="slim-input mb-2" label="Client" v-model="filters.client" :items="filteredClients" :loading="clientsLoading" :search-input.sync="clientSearch" item-text="text" item-value="value" multiple small-chips deletable-chips clearable dense outlined hide-details background-color="white"/>
                    <v-autocomplete class="slim-input mb-2" label="Status" v-model="filters.status" :items="statusOptions" item-text="text" item-value="value" multiple small-chips deletable-chips clearable dense outlined hide-details background-color="white"/>
                    <v-autocomplete class="slim-input mb-2" label="Service" v-model="filters.service" :items="services" multiple small-chips deletable-chips clearable dense outlined hide-details background-color="white"/>
                    <v-text-field class="slim-input mb-2" label="Receiver Name" v-model="filters.name" clearable dense outlined hide-details background-color="white"/>
                    <v-text-field class="slim-input mb-2" label="Email" v-model="filters.email" clearable dense outlined hide-details background-color="white"/>
                    <div class="d-flex">
                      <v-text-field class="slim-input mr-1" label="Phone 1" v-model="filters.phone1" clearable dense outlined hide-details background-color="white"/>
                      <v-text-field class="slim-input ml-1" label="Phone 2" v-model="filters.phone2" clearable dense outlined hide-details background-color="white"/>
                    </div>
                  </div>
                </v-col>
                -->

                <v-col cols="12" md="3">
                  <div class="filter-group-wrapper h-100">
                    <div class="filter-category-tag mb-2">
                      <span class="caption font-weight-bold cyan--text text--darken-2 text-uppercase d-flex align-center">
                        <v-icon x-small left color="cyan darken-2">fas fa-route</v-icon>
                        Shipment & Routing
                      </span>
                    </div>
                    <v-text-field class="slim-input mb-2" label="Tracking No" v-model="filters.tracking" clearable dense outlined hide-details background-color="white"/>
                    <v-text-field class="slim-input mb-2" label="Invoice No" v-model="filters.invoice" clearable dense outlined hide-details background-color="white"/>
                    <v-text-field class="slim-input mb-2" label="Tag Serial" v-model="filters.serial" clearable dense outlined hide-details background-color="white"/>
                    <!-- <v-autocomplete class="slim-input mb-2" label="Routing Rule" v-model="filters.routingRule" :items="routingRuleOptions" clearable dense outlined hide-details background-color="white"/> -->
                    <!-- <v-text-field v-if="isInternalUser" class="slim-input" label="Route Name" v-model="filters.routeName" clearable dense outlined hide-details background-color="white"/> -->
                  </div>
                </v-col>

                <!-- Location filters hidden for now
                <v-col cols="12" md="3">
                  <div class="filter-group-wrapper h-100">
                    <div class="filter-category-tag mb-2">
                      <span class="caption font-weight-bold purple--text text--darken-2 text-uppercase d-flex align-center">
                        <v-icon x-small left color="purple darken-2">fas fa-map-marker-alt</v-icon>
                        Location
                      </span>
                    </div>
                    <v-text-field class="slim-input mb-2" label="Street 1" v-model="filters.street1" clearable dense outlined hide-details background-color="white"/>
                    <v-text-field class="slim-input mb-2" label="Street 2" v-model="filters.street2" clearable dense outlined hide-details background-color="white"/>
                    <v-text-field class="slim-input mb-2" label="Suburb" v-model="filters.suburb" clearable dense outlined hide-details background-color="white"/>
                    <v-row dense>
                      <v-col cols="6">
                        <v-text-field class="slim-input" label="State" v-model="filters.state" clearable dense outlined hide-details background-color="white"/>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field class="slim-input" label="Postcode" v-model="filters.postcode" clearable dense outlined hide-details background-color="white"/>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
                -->
              </v-row>

              <v-row dense class="mt-2">
                <v-col cols="12">
                  <div class="filter-group-wrapper">
                    <div class="filter-category-tag mb-3 d-flex justify-space-between align-center">
                      <span class="caption font-weight-bold pink--text text--darken-2 text-uppercase d-flex align-center">
                        <v-icon x-small left color="pink darken-2">fas fa-calendar-alt</v-icon>
                        Timeline (Date Filters)
                      </span>
                      <span class="caption grey--text font-italic">
                        * Queries limited to max 12 months lookback
                      </span>
                    </div>

                    <v-row dense class="mb-2">
                      <v-col cols="12" md="6">
                        <div class="created-filter-group pa-2 rounded">
                          <div class="caption font-weight-medium grey--text text--darken-1 mb-2">Delivery Date</div>
                          <v-row dense>
                            <v-col cols="6">
                              <v-menu ref="dateMenuFrom" v-model="dateMenuFrom" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    class="slim-input date-picker-field"
                                    v-model="filters.dateFrom"
                                    label="From"
                                    readonly
                                    clearable
                                    v-bind="attrs"
                                    @click.native="openDateMenu($event, 'dateMenuFrom')"
                                    dense outlined hide-details
                                    prepend-inner-icon="mdi-truck-delivery-outline"
                                    background-color="white"
                                  />
                                </template>
                                <v-date-picker v-model="filters.dateFrom" :min="minDateLimit" :max="maxDateLimit" scrollable no-title @input="dateMenuFrom = false"/>
                              </v-menu>
                            </v-col>
                            <v-col cols="6">
                              <v-menu ref="dateMenuTo" v-model="dateMenuTo" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    class="slim-input date-picker-field"
                                    v-model="filters.dateTo"
                                    label="To"
                                    readonly
                                    clearable
                                    v-bind="attrs"
                                    @click.native="openDateMenu($event, 'dateMenuTo')"
                                    dense outlined hide-details
                                    prepend-inner-icon="mdi-calendar-arrow-right"
                                    background-color="white"
                                  />
                                </template>
                                <v-date-picker v-model="filters.dateTo" :min="minDateLimit" scrollable no-title @input="dateMenuTo = false"/>
                              </v-menu>
                            </v-col>
                          </v-row>
                        </div>
                      </v-col>
                      <!-- <v-col cols="12" md="3">
                        <v-menu ref="productionDateMenuFrom" v-model="productionDateMenuFrom" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              class="slim-input date-picker-field"
                              v-model="filters.productionDateFrom"
                              label="Production Date From"
                              readonly
                              v-bind="attrs"
                              @click.native="productionDateMenuFrom = true"
                              dense outlined hide-details
                              prepend-inner-icon="mdi-factory"
                              background-color="white"
                            />
                          </template>
                          <v-date-picker v-model="filters.productionDateFrom" :min="minDateLimit" scrollable no-title @input="productionDateMenuFrom = false"/>
                        </v-menu>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-menu ref="productionDateMenuTo" v-model="productionDateMenuTo" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              class="slim-input date-picker-field"
                              v-model="filters.productionDateTo"
                              label="Production Date To"
                              readonly
                              v-bind="attrs"
                              @click.native="productionDateMenuTo = true"
                              dense outlined hide-details
                              prepend-inner-icon="mdi-calendar-arrow-right"
                              background-color="white"
                            />
                          </template>
                          <v-date-picker v-model="filters.productionDateTo" :min="minDateLimit" scrollable no-title @input="productionDateMenuTo = false"/>
                        </v-menu>
                      </v-col> -->
                    </v-row>

                    <v-row dense>
                      <v-col cols="12" md="6">
                        <div class="created-filter-group pa-2 rounded">
                          <div class="caption font-weight-medium grey--text text--darken-1 mb-2">Created From</div>
                          <v-row dense>
                            <v-col cols="6">
                              <v-menu ref="dateCreatedMenuFrom" v-model="dateCreatedMenuFrom" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    class="slim-input date-picker-field"
                                    v-model="filters.dateCreatedFrom"
                                    label="Date"
                                    readonly
                                    clearable
                                    v-bind="attrs"
                                    @click.native="openDateMenu($event, 'dateCreatedMenuFrom')"
                                    dense outlined hide-details
                                    prepend-inner-icon="mdi-file-document-outline"
                                    background-color="white"
                                  />
                                </template>
                                <v-date-picker v-model="filters.dateCreatedFrom" :min="minDateLimit" scrollable no-title @input="dateCreatedMenuFrom = false"/>
                              </v-menu>
                            </v-col>
                            <v-col cols="6">
                              <v-menu ref="timeCreatedMenuFrom" v-model="timeCreatedMenuFrom" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    class="slim-input date-picker-field"
                                    v-model="filters.timeCreatedFrom"
                                    label="Time"
                                    readonly
                                    clearable
                                    v-bind="attrs"
                                    @click.native="openDateMenu($event, 'timeCreatedMenuFrom')"
                                    dense outlined hide-details
                                    prepend-inner-icon="mdi-clock-start"
                                    background-color="white"
                                  />
                                </template>
                                <v-time-picker v-model="filters.timeCreatedFrom" format="24hr" @click:minutes="timeCreatedMenuFrom = false"/>
                              </v-menu>
                            </v-col>
                          </v-row>
                        </div>
                      </v-col>
                      <v-col cols="12" md="6">
                        <div class="created-filter-group pa-2 rounded">
                          <div class="caption font-weight-medium grey--text text--darken-1 mb-2">Created To</div>
                          <v-row dense>
                            <v-col cols="6">
                              <v-menu ref="dateCreatedMenuTo" v-model="dateCreatedMenuTo" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    class="slim-input date-picker-field"
                                    v-model="filters.dateCreatedTo"
                                    label="Date"
                                    readonly
                                    clearable
                                    v-bind="attrs"
                                    @click.native="openDateMenu($event, 'dateCreatedMenuTo')"
                                    dense outlined hide-details
                                    prepend-inner-icon="mdi-file-document-outline"
                                    background-color="white"
                                  />
                                </template>
                                <v-date-picker v-model="filters.dateCreatedTo" :min="minDateLimit" scrollable no-title @input="dateCreatedMenuTo = false"/>
                              </v-menu>
                            </v-col>
                            <v-col cols="6">
                              <v-menu ref="timeCreatedMenuTo" v-model="timeCreatedMenuTo" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    class="slim-input date-picker-field"
                                    v-model="filters.timeCreatedTo"
                                    label="Time"
                                    readonly
                                    clearable
                                    v-bind="attrs"
                                    @click.native="openDateMenu($event, 'timeCreatedMenuTo')"
                                    dense outlined hide-details
                                    prepend-inner-icon="mdi-clock-end"
                                    background-color="white"
                                  />
                                </template>
                                <v-time-picker v-model="filters.timeCreatedTo" format="24hr" @click:minutes="timeCreatedMenuTo = false"/>
                              </v-menu>
                            </v-col>
                          </v-row>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>

              <v-row class="mt-2 pt-2 border-top-subtle mx-0">
                <v-col cols="12" class="d-flex justify-end align-center px-0">
                  <div class="caption grey--text mr-4 hidden-xs-only">
                    <v-icon x-small color="grey" class="mr-1">mdi-information-outline</v-icon>
                    Results are limited to the last 12 months look-back.
                  </div>
                  <v-btn
                    text
                    @click="resetFilters"
                    :disabled="loading"
                    color="grey darken-2"
                    class="text-capitalize mr-2"
                  >
                    Reset Defaults
                  </v-btn>
                  <v-btn
                    color="primary"
                    @click="applyFilters"
                    :loading="loading"
                    class="text-capitalize px-6"
                    depressed
                  >
                    Apply Filters
                  </v-btn>
                  <v-chip v-if="filtersPending" small color="orange" text-color="white" class="ml-2">
                    Pending
                  </v-chip>
                </v-col>
              </v-row>

              <div v-if="filterPanelExpanded === 0" class="minimize-panel-btn-outer text-center mt-2">
                <v-btn small text color="grey darken-1" @click="minimizeFilterPanel" class="text-capitalize">
                  <v-icon left x-small>mdi-chevron-up</v-icon>
                  Hide Filters
                </v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </div>
      </v-expansion-panels>

      <v-overlay :value="loading" color="white" opacity="0.7" style="z-index: 1000;" fixed>
        <div class="d-flex flex-column align-center">
          <v-progress-circular indeterminate size="48" width="4" color="primary" class="mb-4"></v-progress-circular>
          <div class="subtitle-2 primary--text font-weight-medium mb-3">Loading deliveries...</div>
          <v-btn small outlined color="grey darken-1" @click="cancelLoading" class="text-capitalize">
            Cancel
          </v-btn>
        </div>
      </v-overlay>

      <v-card elevation="0" class="pa-0 overflow-hidden border-subtle rounded-lg">

        <div ref="topScrollWrapper" class="top-scroll-wrapper" @scroll="onTopScroll">
          <div :style="{ width: tableScrollWidth + 'px' }" style="height: 1px;"></div>
        </div>

        <v-data-table
          ref="table"
          :headers="headers"
          :items="deliveries"
          :search="filters.search"
          :loading="loading"
          class="horizontal-scroll-table slim-table"
          dense
          hide-default-footer
          :options.sync="tableOptions"
          :server-items-length="serverItemsLength"
          :footer-props="{
            'items-per-page-options': [10, 20, 50, 100],
            'show-current-page': true,
            'show-first-last-page': true,
            'show-items-per-page': true,
            'disable-pagination': pageCount <= 1
          }"
          @update:options="onTableOptionsChange"
        >
          <template v-slot:top>
            <div class="d-flex align-center pa-3 grey lighten-5 border-bottom-subtle">
              <div class="export-group d-flex align-center pa-2 rounded mr-4" v-if="serverItemsLength > 0">
                <v-btn
                  color="primary"
                  :loading="labelsLoading"
                  depressed
                  small
                  @click.stop="onDownloadLabelsClick"
                  class="text-capitalize mr-2"
                >
                  <v-icon left small>mdi-label-outline</v-icon>
                  Download Labels
                </v-btn>

                <v-select class="slim-input labels-select mr-2" label="Per Page" v-model="labelsCopyCount" :items="[1, 2, 3, 4]" dense outlined hide-details background-color="white"/>
                <!-- <v-select class="slim-input labels-select" label="Barcode Size" v-model="labelsBarcodeSize" :items="barcodeSizeOptions" item-text="text" item-value="value" dense outlined hide-details background-color="white"/> -->
              </div>

              <div class="export-group d-flex align-center pa-2 rounded" v-if="serverItemsLength > 0">
                <v-btn
                  color="success darken-1"
                  :loading="exportLoading"
                  depressed
                  small
                  @click.stop="onDownloadClick"
                  class="text-capitalize mr-2"
                >
                  <v-icon left small>mdi-file-excel-outline</v-icon>
                  Export CSV
                </v-btn>

                <v-checkbox
                  v-model="includeTagData"
                  label="Include tag data"
                  dense
                  hide-details
                  class="mt-0 pt-0 slim-checkbox"
                  color="primary"
                ></v-checkbox>
              </div>

              <v-spacer></v-spacer>

              <v-data-footer
                :options.sync="tableOptions"
                :pagination="paginationForFooter"
                :items-per-page-options="[10, 20, 50, 100]"
                show-current-page
                show-first-last-page
                :disable-pagination="pageCount <= 1"
                @update:options="onTableOptionsChange"
                class="slim-footer"
              />
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div v-if="isDeleted(item)" class="deleted-cell d-flex align-center justify-center">
              <span class="white--text font-weight-bold text-uppercase caption">Deleted</span>
            </div>
            <div v-else class="d-flex justify-center">
              <v-btn icon small color="grey darken-1" @click="viewDelivery(item)" :disabled="loading" title="View Details" class="hover-primary">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:item.tracking="{ item }">
            <span class="primary--text font-weight-bold">{{ item.tracking }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip v-if="isDeleted(item)" small class="font-weight-bold px-2" color="red lighten-5 red--text text--darken-4" label style="height: 24px;">
              Deleted
            </v-chip>
            <v-chip v-else small class="font-weight-bold px-2" :color="getStatusColor(item.status)" label style="height: 24px;">
              {{ getStatusLabel(item.status) }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="pa-8 text-center grey--text text--darken-1">
              <v-icon size="48" color="grey lighten-1" class="mb-3">mdi-clipboard-text-off-outline</v-icon>
              <div class="subtitle-1">No deliveries found</div>
              <div class="caption">Try adjusting your filters or search criteria.</div>
              <div class="caption mt-1">(Remember: Data look-back is limited to the last 12 months)</div>
            </div>
          </template>

          <template v-slot:footer>
            <div class="border-top-subtle d-flex align-center pa-2 grey lighten-5">
              <div class="export-group d-flex align-center pa-2 rounded mr-4" v-if="serverItemsLength > 0">
                <v-btn
                  color="primary"
                  :loading="labelsLoading"
                  depressed
                  small
                  @click.stop="onDownloadLabelsClick"
                  class="text-capitalize mr-2"
                >
                  <v-icon left small>mdi-label-outline</v-icon>
                  Download Labels
                </v-btn>

                <v-select class="slim-input labels-select mr-2" label="Per Page" v-model="labelsCopyCount" :items="[1, 2, 3, 4]" dense outlined hide-details background-color="white"/>
                <!-- <v-select class="slim-input labels-select" label="Barcode Size" v-model="labelsBarcodeSize" :items="barcodeSizeOptions" item-text="text" item-value="value" dense outlined hide-details background-color="white"/> -->
              </div>

              <div class="export-group d-flex align-center pa-2 rounded" v-if="serverItemsLength > 0">
                <v-btn
                  color="success darken-1"
                  :loading="exportLoading"
                  depressed
                  small
                  @click.stop="onDownloadClick"
                  class="text-capitalize mr-2"
                >
                  <v-icon left small>mdi-file-excel-outline</v-icon>
                  Export CSV
                </v-btn>

                <v-checkbox
                  v-model="includeTagData"
                  label="Include tag data"
                  dense
                  hide-details
                  class="mt-0 pt-0 slim-checkbox"
                  color="primary"
                ></v-checkbox>

                <div v-if="exportDeliveries.length > 0" class="ml-2 grey--text caption font-italic">
                  ({{ exportDeliveries.length }} items ready)
                </div>
              </div>

              <v-spacer></v-spacer>
              <v-data-footer
                :options.sync="tableOptions"
                :pagination="paginationForFooter"
                :items-per-page-options="[10, 20, 50, 100]"
                show-current-page
                show-first-last-page
                :disable-pagination="pageCount <= 1"
                @update:options="onTableOptionsChange"
                class="slim-footer"
              />
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <DeliveryInfoDisplayer
      :tracking="selectedTrackingNumber"
      :showProp="showInfo"
      v-on:close="showInfo=false"
      :displayShowDialog="displayShowDialog"
      :closeShowDialog="closeShowDialog"
    ></DeliveryInfoDisplayer>

    <v-dialog v-model="exportLoading" persistent max-width="450">
      <v-card class="pa-0 rounded-lg overflow-hidden">
        <v-card-title class="primary white--text py-3 px-4 title font-weight-bold d-flex align-center">
          <v-icon color="white" class="mr-3">mdi-cloud-download</v-icon>
          Exporting Data
        </v-card-title>
        <v-card-text class="pt-6 px-6 pb-4 text-center">
          <div class="mb-6 title grey--text text--darken-3">
            Generating CSV File
          </div>
          <v-progress-circular
            :rotate="-90"
            :size="120"
            :width="12"
            :value="exportProgress"
            color="primary"
            class="mb-4"
          >
            <div class="d-flex flex-column align-center">
              <span class="title font-weight-bold primary--text">{{ Math.ceil(exportProgress) }}%</span>
              <span class="caption grey--text">Complete</span>
            </div>
          </v-progress-circular>

          <div class="caption grey--text mb-4">
            Fetching chunks... please wait.
          </div>

          <div class="d-flex justify-center mb-4" style="gap: 24px;">
            <div class="d-flex flex-column align-center">
              <span class="caption grey--text text--darken-1 font-weight-bold text-uppercase" style="letter-spacing: 0.5px;">Elapsed</span>
              <span class="subtitle-2 font-weight-medium grey--text text--darken-3">{{ exportElapsedFormatted }}</span>
            </div>
            <div v-if="exportRemainingFormatted" class="d-flex flex-column align-center">
              <span class="caption grey--text text--darken-1 font-weight-bold text-uppercase" style="letter-spacing: 0.5px;">Est. Remaining</span>
              <span class="subtitle-2 font-weight-medium grey--text text--darken-3">{{ exportRemainingFormatted }}</span>
            </div>
          </div>

          <v-btn outlined color="error" @click="cancelExport" class="text-capitalize px-6">
            Cancel Download
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Papa from 'papaparse';
import debounce from 'lodash/debounce'
import DeliveryInfoDisplayer from "./Search/DeliveryInfoDisplayer";

export default {
  name: 'DeliveryListing',
  components: {
    DeliveryInfoDisplayer
  },
  data() {
    return {
      filterPanelExpanded: [0],
      showInfo: false,
      selectedTrackingNumber: null,
      filters: {
        routingRule: null,
        routeName: '',
        consolidationDepot: null,
        country: null,
        region: null,
        depot: null,
        client: [],
        service: [],
        tracking: '',
        serial: '',
        invoice: '',
        suburb: '',
        postcode: '',
        name: '',
        street1: '',
        street2: '',
        state: '',
        phone1: '',
        phone2: '',
        email: '',
        dateFrom: null, // Initialized in mounted/reset
        dateTo: null,
        dateCreatedFrom: null,
        timeCreatedFrom: null,
        dateCreatedTo: null,
        timeCreatedTo: null,
        productionDateFrom: null,
        productionDateTo: null,
        status: [],
        source: 'all',
        deleted: 'all'
      },
      // Stores the last set of filters that were applied (on Apply Filters or Reset All).
      lastAppliedFilters: null,

      routingRuleOptions: [
        'Day', 'Overnight'
      ],
      countryOptions: [
        { text: 'Australia', value: 'AUS' },
        { text: 'New Zealand', value: 'NZL' }
      ],

      statusOptions: [
        { text: 'Delivered', value: 'Processed' },
        { text: 'Canceled', value: 'Canceled' },
        { text: 'Pending', value: 'Pending' },
        { text: 'On Hold', value: 'On Hold' },
        { text: 'Rescheduled', value: 'Rescheduled' }
      ],

      allRegions: [],
      allDepots: [],
      allClimates: [],
      clients: [],
      services: [],
      deliveries: [],
      exportDeliveries: [],
      exportLoading: false,
      exportProgress: 0,
      serverItemsLength: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 50,
        sortBy: ['date'],
        sortDesc: [true]
      },
      pagination: {
        page: 1
      },
      headers: [
        { text: 'Actions', value: 'actions', align: 'center', sortable: false, width: '80px', fixed: true },
        { text: 'Tracking No', value: 'tracking', align: 'center', sortable: true, width: '160px' },
        { text: 'Invoice No', value: 'invoice', align: 'center', sortable: true, width: '140px' },
        { text: 'Manifest ID', value: 'manifest', align: 'center', sortable: true, width: '140px' },
        { text: 'Date', value: 'date', align: 'center', sortable: true, width: '130px' },
        { text: 'Status', value: 'status', align: 'center', sortable: true, width: '140px' },
        { text: 'Client', value: 'client_name', align: 'left', sortable: true, width: '220px' },
        { text: 'Depot', value: 'depot_name', align: 'center', sortable: true, width: '140px' },
        { text: 'Zone', value: 'zone_name', align: 'center', sortable: true, width: '140px' },
        { text: 'Service', value: 'service_name', align: 'center', sortable: true, width: '140px' },
        { text: 'Created', value: 'created', align: 'center', sortable: true, width: '170px' },
        { text: 'Name', value: 'name', align: 'left', sortable: true, width: '180px' },
        { text: 'Suburb', value: 'suburb', align: 'left', sortable: true, width: '150px' },
        { text: 'State', value: 'state', align: 'center', sortable: true, width: '90px' },
        { text: 'Postcode', value: 'postcode', align: 'center', sortable: true, width: '100px' },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false, width: '80px' }
      ],
      searchInput: '',
      dateMenuFrom: false,
      dateMenuTo: false,
      dateCreatedMenuFrom: false,
      timeCreatedMenuFrom: false,
      dateCreatedMenuTo: false,
      timeCreatedMenuTo: false,
      productionDateMenuFrom: false,
      productionDateMenuTo: false,
      sourceOptions: [
        { text: 'All: API and Manifest', value: 'all' },
        { text: 'Manifest', value: 'manifest' },
        { text: 'API', value: 'api' }
      ],
      deletedOptions: [
        { text: 'All Deliveries', value: 'all' },
        { text: 'Non-Deleted Only', value: false },
        { text: 'Deleted Only', value: true }
      ],
      depotsLoading: false,
      clientsLoading: false,
      regionsLoading: false,
      loading: false,
      labelsLoading: false,
      clientSearch: null,
      depotSearch: null,
      consolidationDepotSearch: null,
      regionSearch: null,
      includeTagData: false,
      labelsCopyCount: 4,
      labelsBarcodeSize: 5,
      barcodeSizeOptions: [
        { text: 'Small', value: 1 },
        { text: 'Medium', value: 2 },
        { text: 'Large', value: 3 },
        { text: 'Small X', value: 5 },
        { text: 'Small XX', value: 6 }
      ],
      cancelTokenSource: null,
      exportCancelTokenSource: null,
      isExportCancelled: false,
      _autoSelectingCountry: false,
      exportStartTime: null,
      exportElapsedSeconds: 0,
      exportTimerInterval: null,
      tableScrollWidth: 0,
      tableWrapperElement: null,
      isSyncingLeft: false
    }
  },
  computed: {
    isInternalUser: function() {
      return this.$store.getters['auth/isInternalUser'];
    },
    exportElapsedFormatted: function() {
      var s = this.exportElapsedSeconds;
      var mins = Math.floor(s / 60);
      var secs = s % 60;
      return (mins < 10 ? '0' : '') + mins + ':' + (secs < 10 ? '0' : '') + secs;
    },
    exportRemainingFormatted: function() {
      if (this.exportProgress <= 0 || this.exportElapsedSeconds < 2) return null;
      var fraction = this.exportProgress / 100;
      if (fraction >= 1) return '00:00';
      var estimatedTotal = this.exportElapsedSeconds / fraction;
      var remaining = Math.max(0, Math.round(estimatedTotal - this.exportElapsedSeconds));
      var mins = Math.floor(remaining / 60);
      var secs = remaining % 60;
      return (mins < 10 ? '0' : '') + mins + ':' + (secs < 10 ? '0' : '') + secs;
    },
    // True when the current UI filters differ from the last applied filters
    filtersPending: function() {
      if (!this.lastAppliedFilters) return false;
      try {
        return JSON.stringify(this.filters) !== JSON.stringify(this.lastAppliedFilters);
      } catch (e) {
        return false;
      }
    },
    // Helper to calculate Today - 12 Months
    minDateLimit: function() {
      var d = new Date();
      d.setFullYear(d.getFullYear() - 1);
      return d.toISOString().substring(0, 10);
    },
    // Helper to calculate Today + 3 Months (max future date for delivery date FROM)
    maxDateLimit: function() {
      var d = new Date();
      d.setMonth(d.getMonth() + 3);
      return d.toISOString().substring(0, 10);
    },

    filteredRegions: function() {
      if (!this.filters.country) return this.allRegions;
      return this.allRegions.filter(function(r) { return r.country === this.filters.country; }.bind(this));
    },
    filteredDepots: function() {
      var filtered = this.allDepots;
      if (this.filters.region) {
        var regionId = this.filters.region;
        filtered = filtered.filter(function(d) { return d.region_id === regionId; });
      } else if (this.filters.country) {
        var country = this.filters.country;
        var regionIdsForCountry = this.allRegions
          .filter(function(r) { return r.country === country; })
          .map(function(r) { return r.id; });
        filtered = filtered.filter(function(d) { return regionIdsForCountry.indexOf(d.region_id) !== -1; });
      }
      return filtered;
    },
    filteredConsolidationDepots: function() {
      return this.filteredDepots
        .filter(function(d) { return d.parent_depot == null; })
        .map(function(d) { return { text: d.id + ' - ' + d.name, value: d.id }; });
    },
    filteredClients: function() {
      if (!this.filters.client || !this.filters.client.length) return this.clients;
      var firstClientId = this.filters.client[0];
      var firstClient = this.clients.find(function(c) { return c.value === firstClientId; });
      if (!firstClient || !firstClient.country) return this.clients;
      var country = firstClient.country;
      return this.clients.filter(function(c) { return c.country === country; });
    },
    appliedFilters: function() {
      var map = {
        routingRule: 'Routing Rule', routeName: 'Route Name', consolidationDepot: 'Consolidation Depot', country: 'Country', region: 'Region', depot: 'Depot',
        client: 'Client', service: 'Service', tracking: 'Tracking No', serial: 'Serial No',
        invoice: 'Invoice No', suburb: 'Suburb', postcode: 'Postcode', status: 'Status', source: 'Source', deleted: 'Deleted',
        name: 'Name', street1: 'Street 1', street2: 'Street 2', state: 'State', phone1: 'Phone 1', phone2: 'Phone 2', email: 'Email',
        dateFrom: 'Delivery Date From', dateTo: 'Delivery Date To',
        dateCreatedFrom: 'Date Created From', timeCreatedFrom: 'Time Created From',
        dateCreatedTo: 'Date Created To', timeCreatedTo: 'Time Created To',
        productionDateFrom: 'Production Date From', productionDateTo: 'Production Date To'
      };
      var filters = this.filters;
      var clients = this.clients;
      var services = this.services;
      var statusOptions = this.statusOptions;
      var deletedOptions = this.deletedOptions;
      var sourceOptions = this.sourceOptions;
      return Object.keys(filters)
        .filter(function(key) {
          var val = filters[key];
          if (key === 'deleted') return false; // hidden for now; defaults to all
          if (Array.isArray(val)) return val.length > 0;
          return val && key !== 'search';
        })
        .map(function(key) {
          var val = filters[key];
          var displayValue = val;
          if (key === 'deleted') {
            var opt = deletedOptions.find(function(o) { return o.value === val; });
            displayValue = opt ? opt.text : val;
          } else if (key === 'source') {
            displayValue = val === 'all' ? 'All: API and Manifest' : (sourceOptions.find(function(o) { return o.value === val; }) || {}).text || val;
          } else if (Array.isArray(val)) {
            if (key === 'client') {
              displayValue = val.map(function(v) {
                var found = clients.find(function(c) { return c.value === v; });
                return found ? found.text : v;
              }).join(', ');
            } else if (key === 'service') {
              displayValue = val.map(function(v) {
                var found = services.find(function(s) { return s.value === v; });
                return found ? found.text : v;
              }).join(', ');
            } else if (key === 'status') {
              displayValue = val.map(function(v) {
                var found = statusOptions.find(function(s) { return s.value === v; });
                return found ? found.text : v;
              }).join(', ');
            } else {
              displayValue = val.join(', ');
            }
          }
          return { key: key, label: map[key] || key, value: displayValue };
        });
    },
    canClearFilters: function() {
      return this.appliedFilters.some(function(f) {
        if (f.key === 'source') return f.value !== 'All: API and Manifest';
        if (f.key === 'deleted') return f.value !== 'All Deliveries';
        return true;
      });
    },
    pageCount: function() {
      if (!this.serverItemsLength || !this.tableOptions.itemsPerPage) return 1;
      return Math.ceil(this.serverItemsLength / this.tableOptions.itemsPerPage) || 1;
    },
    paginationForFooter: function() {
      const itemsLength = this.serverItemsLength || 0;
      const itemsPerPage = this.tableOptions.itemsPerPage || 10;
      const page = this.tableOptions.page || 1;
      return {
        page: page,
        itemsPerPage: itemsPerPage,
        pageStart: (page - 1) * itemsPerPage,
        pageStop: Math.min(page * itemsPerPage, itemsLength),
        itemsLength: itemsLength,
        pageCount: this.pageCount,
        lastPage: this.pageCount
      };
    }
  },
    methods: {
    // Clear all date filters if tracking, invoice, or serial is set
    clearDatesIfSpecialFilters: function() {
      if ((this.filters.tracking && this.filters.tracking !== '') ||
          (this.filters.invoice && this.filters.invoice !== '') ||
          (this.filters.serial && this.filters.serial !== '')) {
        this.filters.dateFrom = null;
        this.filters.dateTo = null;
        this.filters.dateCreatedFrom = null;
        this.filters.timeCreatedFrom = null;
        this.filters.dateCreatedTo = null;
        this.filters.timeCreatedTo = null;
        this.filters.productionDateFrom = null;
        this.filters.productionDateTo = null;
      }
    },

    minimizeFilterPanel: function() { this.filterPanelExpanded = []; },

    openDateMenu: function(event, menuKey) {
      if (event.target.closest('.v-input__icon--clear')) return;
      this[menuKey] = true;
    },

    applyFilters: function() {
      var hasSpecialFilter = (this.filters.tracking && this.filters.tracking !== '') ||
          (this.filters.invoice && this.filters.invoice !== '') ||
          (this.filters.serial && this.filters.serial !== '');

      if (!hasSpecialFilter) {
        var hasDateFilter = this.filters.dateFrom || this.filters.dateTo ||
            this.filters.dateCreatedFrom || this.filters.dateCreatedTo ||
            this.filters.productionDateFrom || this.filters.productionDateTo;
        if (!hasDateFilter) {
          this.$store.commit('displaySnackbar', 'At least one date filter is required (e.g. Created From).');
          return;
        }
      }

      // Ensure pagination resets when applying new filters
      if (this.tableOptions.page !== 1) this.tableOptions.page = 1;
      if (this.debouncedLoadDeliveries) this.debouncedLoadDeliveries.cancel();
      // markApplied=true => update lastAppliedFilters after successful load
      this.loadDeliveries(this.filters, this.tableOptions, true);
    },

    cancelLoading: function() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('Operation canceled by user.');
        this.loading = false;
      }
    },

    // Helper to get date string for 1 month ago
    getLastMonthDate: function() {
      var d = new Date();
      d.setMonth(d.getMonth() - 1);
      return d.toISOString().substring(0, 10);
    },

    // Helper to get today's date string
    getTodayDate: function() {
      return new Date().toISOString().substring(0, 10);
    },

    loadDeliveries: function(filters, options, markApplied) {
      if (!filters) filters = this.filters;
      if (!options) options = this.tableOptions;

      // Cancel previous request if it exists
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('Operation canceled by new request.');
      }
      this.cancelTokenSource = axios.CancelToken.source();

      this.loading = true;
      var url = process.env.API_URL + '/deliveries/list';

      // If tracking, invoice, or serial is set, make all date filters null
      var trackingActive = filters.tracking && filters.tracking !== '';
      var invoiceActive = filters.invoice && filters.invoice !== '';
      var serialActive = filters.serial && filters.serial !== '';

      var params = {
        source: filters.source,
        deleted: filters.deleted === 'all' ? null : filters.deleted,
        status: (Array.isArray(filters.status) && filters.status.length) ? filters.status.join(',') : filters.status,
        country: filters.country,
        suburb: filters.suburb,
        postcode: filters.postcode,
        tracking: filters.tracking,
        tag_serial: filters.serial,
        invoice: filters.invoice,
        client_id: (Array.isArray(filters.client) && filters.client.length) ? filters.client.join(',') : filters.client,
        service_id: (Array.isArray(filters.service) && filters.service.length) ? filters.service.join(',') : filters.service,
        region_id: filters.region,
        depot_id: filters.depot,
        consolidation_depot_id: filters.consolidationDepot,
        routing_rule: filters.routingRule,
        route_name: filters.routeName,
        name: filters.name,
        street1: filters.street1,
        street2: filters.street2,
        state: filters.state,
        phone1: filters.phone1,
        phone2: filters.phone2,
        email: filters.email,
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        created_from: filters.dateCreatedFrom ? (filters.dateCreatedFrom + (filters.timeCreatedFrom ? ' ' + filters.timeCreatedFrom : '')) : null,
        created_to: filters.dateCreatedTo ? (filters.dateCreatedTo + (filters.timeCreatedTo ? ' ' + filters.timeCreatedTo : '')) : null,
        production_date_from: filters.productionDateFrom,
        production_date_to: filters.productionDateTo,
        sort_by: (options.sortBy && options.sortBy.length) ? options.sortBy[0] : null,
        sort_direction: (options.sortDesc && options.sortDesc.length && options.sortDesc[0]) ? 'desc' : 'asc',
        page_number: options.page || 1, // CORRECTED HERE
        page_size: options.itemsPerPage || 20
      };

      if (trackingActive || invoiceActive || serialActive) {
        params.date_from = null;
        params.date_to = null;
        params.created_from = null;
        params.created_to = null;
        params.production_date_from = null;
        params.production_date_to = null;
      }

      // Remove empty-string/null/undefined params so backend doesn't treat them as active filters
      Object.keys(params).forEach(function(k) {
        if (params[k] === '' || params[k] === null || params[k] === undefined || (Array.isArray(params[k]) && params[k].length === 0)) {
          delete params[k];
        }
      });

      axios.get(url, {
        params: params,
        cancelToken: this.cancelTokenSource.token
      })
        .then(function(resp) {
          this.deliveries = (resp.data && resp.data.data) ? resp.data.data : [];
          this.serverItemsLength = (resp.data && resp.data.total) ? resp.data.total : 0;
          // If this load corresponds to an explicit Apply/Reset, snapshot filters as applied
          if (markApplied) {
            try {
              this.lastAppliedFilters = JSON.parse(JSON.stringify(filters));
            } catch (e) {
              this.lastAppliedFilters = Object.assign({}, filters);
            }
          }
          this.loading = false;

          // Re-calculate top scrollbar after data loads
          this.$nextTick(function() {
            this.updateScrollbarWidth();
          }.bind(this));
        }.bind(this))
        .catch(function(thrown) {
          if (axios.isCancel(thrown)) {
            console.log('Request canceled', thrown.message);
          } else {
            this.deliveries = [];
            this.serverItemsLength = 0;
            this.loading = false;
          }
        }.bind(this));
    },

    onTableOptionsChange: function(options) {
      this.tableOptions = options;
      this.loading = true;
      this.debouncedLoadDeliveries();
    },

    resetFilters: function() {
      // Default to today's date (query from current delivery date onwards)
      var today = this.getTodayDate();

      // For external users, preserve their client's country
      var country = null;
      if (!this.isInternalUser) {
        var client = this.$store.state.auth.client;
        if (client && client.default_country) {
          country = client.default_country;
        }
      }

      this.filters = {
        routingRule: null, routeName: '', consolidationDepot: null, country: country, region: null, depot: null,
        client: [], service: [], tracking: '', serial: '', invoice: '', suburb: '', postcode: '',
        name: '', street1: '', street2: '', state: '', phone1: '', phone2: '', email: '',
        dateFrom: today, dateTo: null,
        dateCreatedFrom: null, timeCreatedFrom: null,
        dateCreatedTo: null, timeCreatedTo: null,
        productionDateFrom: null, productionDateTo: null,
        status: [], source: 'all', deleted: 'all', search: ''
      };
      this.searchInput = '';
      this.tableOptions.page = 1;
      this.tableOptions.sortBy = ['date'];
      this.tableOptions.sortDesc = [true];
      this.pagination.page = 1;
      // Reset should auto-load and mark filters as applied
      this.loadDeliveries(this.filters, this.tableOptions, true);
    },

    removeFilter: function(key) {
      if (key === 'source') {
        this.filters.source = 'all';
      } else if (key === 'deleted') {
        this.filters.deleted = 'all';
      } else if (key === 'client' || key === 'status' || key === 'service') {
        this.filters[key] = [];
      } else {
        this.filters[key] = (key === 'search' ? '' : null);
      }
    },

    isDeleted: function(item) {
      if (!item) return false;
      return item.deleted === true || item.deleted === 1 ||
        item.deleted === '1' || item.deleted === 'true';
    },

    viewDelivery: function(row) {
      this.selectedTrackingNumber = row.tracking;
      this.showInfo = true;
    },
    displayShowDialog: function(){
      this.showInfo = true;
    },
    closeShowDialog: function(){
      this.showInfo = false;
    },
    getStatusLabel: function(status) {
      if (typeof status === 'string' && status.toLowerCase() === 'processed') {
        return 'Delivered';
      }
      return status;
    },
    getStatusColor: function(status) {
      if (typeof status === 'string' && status.toLowerCase() === 'processed') {
        return 'green lighten-5 green--text text--darken-4';
      }
      const colors = {
        'Processed': 'blue lighten-5 blue--text text--darken-3',
        'Canceled': 'grey lighten-4 grey--text text--darken-1',
        'Pending': 'orange lighten-5 orange--text text--darken-4',
        'On Hold': 'red lighten-5 red--text text--darken-4',
        'Rescheduled': 'purple lighten-5 purple--text text--darken-3',
        'Delivered': 'green lighten-5 green--text text--darken-4',
        'Failed Delivery': 'error lighten-5 error--text text--darken-4'
      };
      return colors[status] || 'grey lighten-5';
    },
    onDownloadLabelsClick: function() {
      if (this.serverItemsLength === 0) {
        this.$store.commit('displaySnackbar', 'No deliveries to generate labels for.');
        return;
      }

      var filters = this.filters;
      var trackingActive = filters.tracking && filters.tracking !== '';
      var invoiceActive = filters.invoice && filters.invoice !== '';
      var serialActive = filters.serial && filters.serial !== '';

      var labelFilters = {
        source: filters.source,
        deleted: filters.deleted === 'all' ? null : filters.deleted,
        status: (Array.isArray(filters.status) && filters.status.length) ? filters.status.join(',') : filters.status,
        country: filters.country,
        suburb: filters.suburb,
        postcode: filters.postcode,
        tracking: filters.tracking,
        tag_serial: filters.serial,
        invoice: filters.invoice,
        client_id: (Array.isArray(filters.client) && filters.client.length) ? filters.client.join(',') : filters.client,
        service_id: (Array.isArray(filters.service) && filters.service.length) ? filters.service.join(',') : filters.service,
        region_id: filters.region,
        depot_id: filters.depot,
        consolidation_depot_id: filters.consolidationDepot,
        routing_rule: filters.routingRule,
        route_name: filters.routeName,
        name: filters.name,
        street1: filters.street1,
        street2: filters.street2,
        state: filters.state,
        phone1: filters.phone1,
        phone2: filters.phone2,
        email: filters.email,
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        created_from: filters.dateCreatedFrom ? (filters.dateCreatedFrom + (filters.timeCreatedFrom ? ' ' + filters.timeCreatedFrom : '')) : null,
        created_to: filters.dateCreatedTo ? (filters.dateCreatedTo + (filters.timeCreatedTo ? ' ' + filters.timeCreatedTo : '')) : null,
        production_date_from: filters.productionDateFrom,
        production_date_to: filters.productionDateTo
      };

      if (trackingActive || invoiceActive || serialActive) {
        labelFilters.date_from = null;
        labelFilters.date_to = null;
        labelFilters.created_from = null;
        labelFilters.created_to = null;
        labelFilters.production_date_from = null;
        labelFilters.production_date_to = null;
      }

      Object.keys(labelFilters).forEach(function(k) {
        if (labelFilters[k] === '' || labelFilters[k] === null || labelFilters[k] === undefined) {
          delete labelFilters[k];
        }
      });

      this.labelsLoading = true;
      axios({
        url: process.env.API_URL + '/deliveries/labels',
        method: 'POST',
        responseType: 'blob',
        data: {
          filters: labelFilters,
          number: this.labelsCopyCount,
          barcodeSize: this.labelsBarcodeSize
        }
      })
        .then(function(response) {
          var blob = new Blob([response.data], { type: 'application/pdf' });
          var url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(function() { URL.revokeObjectURL(url); }, 60000);
        }.bind(this))
        .catch(function(error) {
          var msg = 'Failed to generate labels.';
          if (error.response && error.response.status === 404) {
            msg = 'No labels found for the selected deliveries.';
          }
          this.$store.commit('displaySnackbar', msg);
        }.bind(this))
        .finally(function() {
          this.labelsLoading = false;
        }.bind(this));
    },

    onDownloadClick: function() {
      if (this.exportLoading) return;
      this.exportToCsv();
    },

    autoSelectCountryFor: function(filterKey, value) {
      if (this.filters.country || !value) return;
      if (Array.isArray(value) && value.length === 0) return;
      var country = null;
      if (filterKey === 'client') {
        var clientVal = Array.isArray(value) ? value[0] : value;
        var client = this.clients.find(function(c) { return c.value === clientVal; });
        if (client) country = client.country;
      } else if (filterKey === 'region') {
        var region = this.allRegions.find(function(r) { return r.id === value; });
        if (region) country = region.country;
      } else if (filterKey === 'depot' || filterKey === 'consolidationDepot') {
        var depot = this.allDepots.find(function(d) { return d.id === value; });
        if (depot) {
          var depotRegion = this.allRegions.find(function(r) { return r.id === depot.region_id; });
          if (depotRegion) country = depotRegion.country;
        }
      }
      if (country) {
        this._autoSelectingCountry = true;
        this.filters.country = country;
        this.$nextTick(function() { this._autoSelectingCountry = false; }.bind(this));
      }
    },

    loadClients: function(search) {
      this.clientsLoading = true;
      var q = search ? '?q=' + encodeURIComponent(search) : '';
      axios.get(process.env.API_URL + '/clients' + q)
        .then(function(resp) {
          var data = resp.data || [];
          if (this.filters.country) {
            var country = this.filters.country;
            data = data.filter(function(c) { return c.default_country === country; });
          }
          this.clients = data.map(function(c) { return { text: c.id + ' - ' + c.name, value: c.id, country: c.default_country || null }; });
        }.bind(this)).finally(function() { this.clientsLoading = false; }.bind(this));
    },

    loadDepots: function(search) {
      this.depotsLoading = true;
      var q = search ? '?q=' + encodeURIComponent(search) : '';
      axios.get(process.env.API_URL + '/depots' + q)
        .then(function(resp) {
          this.allDepots = resp.data || [];
        }.bind(this)).finally(function() { this.depotsLoading = false; }.bind(this));
    },

    loadRegions: function(search) {
      this.regionsLoading = true;
      var q = search ? '?q=' + encodeURIComponent(search) : '';
      axios.get(process.env.API_URL + '/regions' + q)
        .then(function(resp) { this.allRegions = resp.data || []; }.bind(this))
        .finally(function() { this.regionsLoading = false; }.bind(this));
    },

    loadServices: function() {
      axios.get(process.env.API_URL + '/services')
        .then(function(resp) {
          this.services = (resp.data || []).map(function(s) { return { text: s.id + ' - ' + s.name, value: s.id }; });
        }.bind(this));
    },

    loadClimates: function() {
      axios.get(process.env.API_URL + '/climates')
        .then(function(resp) {
          this.allClimates = resp.data || [];
        }.bind(this));
    },

    cancelExport: function() {
      if (this.exportCancelTokenSource) {
        this.exportCancelTokenSource.cancel('Export cancelled by user');
      }
      this.isExportCancelled = true;
      this.exportLoading = false;
      this.exportProgress = 0;
      if (this.exportTimerInterval) {
        clearInterval(this.exportTimerInterval);
        this.exportTimerInterval = null;
      }
    },

    exportToCsv: async function() {
      this.exportLoading = true;
      this.exportProgress = 0;
      this.exportDeliveries = [];
      this.isExportCancelled = false;
      this.exportCancelTokenSource = axios.CancelToken.source();
      // Start elapsed time timer
      this.exportStartTime = Date.now();
      this.exportElapsedSeconds = 0;
      if (this.exportTimerInterval) clearInterval(this.exportTimerInterval);
      this.exportTimerInterval = setInterval(function() {
        this.exportElapsedSeconds = Math.floor((Date.now() - this.exportStartTime) / 1000);
      }.bind(this), 1000);
      let exportError = null;
      let apiError = null;
      var MAX_PARALLEL = 5;
      // Recursively sanitize params: convert objects to IDs or null, handle arrays
      function deepSanitize(val) {
        if (val === undefined) return null;
        if (val === null) return null;
        if (Array.isArray(val)) {
          // Only keep array if all elements are primitive
          return val.map(deepSanitize).filter(v => (typeof v !== 'object' || v === null));
        }
        if (typeof val === 'object') {
          if ('value' in val) return val.value;
          if ('id' in val) return val.id;
          // If it's a plain object, flatten to null (axios cannot serialize objects)
          return null;
        }
        return val;
      }
      var self = this;
      var filters = JSON.parse(JSON.stringify(this.filters));
      Object.keys(filters).forEach(function(k) {
        filters[k] = deepSanitize(filters[k]);
      });
      var options = JSON.parse(JSON.stringify(this.tableOptions));
      var url = process.env.API_URL + '/deliveries/list';
      var allData = [];
      var allTags = [];
      var pageSize = 100;
      var totalRecords = 0;

      function buildParams(pageNumber) {
        var params = {
          source: filters.source,
          deleted: filters.deleted === 'all' ? null : String(filters.deleted),
          status: (Array.isArray(filters.status) && filters.status.length) ? filters.status.join(',') : filters.status,
          country: filters.country,
          suburb: filters.suburb,
          postcode: filters.postcode,
          tracking: filters.tracking,
          tag_serial: filters.serial,
          invoice: filters.invoice,
          client_id: (Array.isArray(filters.client) && filters.client.length) ? filters.client.join(',') : filters.client,
          service_id: (Array.isArray(filters.service) && filters.service.length) ? filters.service.join(',') : filters.service,
          region_id: filters.region,
          depot_id: filters.depot,
          consolidation_depot_id: filters.consolidationDepot,
          routing_rule: filters.routingRule,
          route_name: filters.routeName,
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
          created_from: filters.dateCreatedFrom ? (filters.dateCreatedFrom + (filters.timeCreatedFrom ? ' ' + filters.timeCreatedFrom : '')) : null,
          created_to: filters.dateCreatedTo ? (filters.dateCreatedTo + (filters.timeCreatedTo ? ' ' + filters.timeCreatedTo : '')) : null,
          production_date_from: filters.productionDateFrom,
          production_date_to: filters.productionDateTo,
          sort_by: (options.sortBy && options.sortBy.length) ? options.sortBy[0] : null,
          sort_direction: (options.sortDesc && options.sortDesc.length && options.sortDesc[0]) ? 'desc' : 'asc',
          page_number: pageNumber,
          page_size: pageSize,
          include_tags: self.includeTagData ? 1 : 0,
          get_tags: self.includeTagData
        };
        // Final check: remove any params that are not string, number, boolean, or array of those
        Object.keys(params).forEach(function(k) {
          const v = params[k];
          if (
            v === '' ||
            v === null ||
            v === undefined ||
            (typeof v === 'object' && !Array.isArray(v)) ||
            (Array.isArray(v) && v.some(e => typeof e !== 'string' && typeof e !== 'number'))
          ) {
            delete params[k];
          } else if (typeof v === 'boolean') {
            params[k] = v ? 1 : 0;
          }
        });
        return params;
      }

      try {
        // --- Fetch first page to determine totalRecords ---
        if (this.isExportCancelled) return [];
        var firstParams = buildParams(1);
        if (!firstParams || typeof firstParams !== 'object' || Array.isArray(firstParams)) {
          if (window && window.console) {
            console.error('Export params is not a plain object:', firstParams);
          }
          exportError = new Error('Export params is not a plain object');
        }
        if (!exportError) {
          try {
            var firstResp = await axios.get(url, {
              params: firstParams,
              cancelToken: this.exportCancelTokenSource.token
            });
            if (!firstResp || !firstResp.data || !firstResp.data.data) {
              apiError = 'API did not return expected data.';
            }
          } catch (err) {
            exportError = err;
            apiError = err && err.message ? err.message : 'API error';
          }
        }
        if (!exportError && !apiError) {
          var firstData = (firstResp.data && firstResp.data.data) ? firstResp.data.data : [];
          var firstTags = (firstResp.data && firstResp.data.tags) ? firstResp.data.tags : [];
          totalRecords = (firstResp.data && firstResp.data.total) ? firstResp.data.total : 0;
          allData = allData.concat(firstData);
          allTags = allTags.concat(firstTags);
          this.exportProgress = totalRecords > 0 ? (allData.length / totalRecords) * 100 : 0;

          // --- Fetch remaining pages in parallel batches ---
          if (allData.length < totalRecords && firstData.length > 0 && !this.isExportCancelled) {
            var totalPages = Math.ceil(totalRecords / pageSize);
            var remainingPages = [];
            for (var p = 2; p <= totalPages; p++) {
              remainingPages.push(p);
            }
            // Process in batches of MAX_PARALLEL
            for (var i = 0; i < remainingPages.length; i += MAX_PARALLEL) {
              if (this.isExportCancelled) break;
              var batch = remainingPages.slice(i, i + MAX_PARALLEL);
              var promises = batch.map(function(pageNum) {
                return axios.get(url, {
                  params: buildParams(pageNum),
                  cancelToken: self.exportCancelTokenSource.token
                });
              });
              try {
                var results = await Promise.all(promises);
              } catch (err) {
                exportError = err;
                apiError = err && err.message ? err.message : 'API error';
                break;
              }
              for (var j = 0; j < results.length; j++) {
                var resp = results[j];
                if (!resp || !resp.data || !resp.data.data) {
                  apiError = 'API did not return expected data.';
                  break;
                }
                var data = resp.data.data || [];
                var tags = resp.data.tags || [];
                allData = allData.concat(data);
                allTags = allTags.concat(tags);
              }
              if (apiError) break;
              this.exportProgress = totalRecords > 0 ? Math.min((allData.length / totalRecords) * 100, 100) : 0;
            }
          }
        }
        if (this.isExportCancelled) return [];
        var tagsByStopId = {};
        if (this.includeTagData) {
          allTags.forEach(function(tag) {
            if (!tagsByStopId[tag.stop]) {
              tagsByStopId[tag.stop] = [];
            }
            tagsByStopId[tag.stop].push(tag);
          });
        }
        const mappedData = [];
        // Build lookup maps for climate and consolidation depot names
        var climateMap = {};
        (this.allClimates || []).forEach(function(c) { climateMap[c.id] = c.name; });
        var depotMap = {};
        (this.allDepots || []).forEach(function(d) { depotMap[d.id] = d.name; });

        allData.forEach(function(d) {
          var baseRow = {
            'Tracking No': d.tracking,
            'Invoice No': d.invoice,
            'Manifest ID': d.manifest,
            'Depot': d.depot_name,
            'Client': d.client_name,
            'Service': d.service_name,
            'Routing Rule': d.routing_rule,
            'Date': d.date,
            'Status': d.status,
            'Created': d.created,
            'Name': d.name,
            'Street 1': d.street1,
            'Street 2': d.street2,
            'Suburb': d.suburb,
            'State': d.state,
            'Postcode': d.postcode,
            'Phone 1': d.phone1,
            'Phone 2': d.phone2,
            'Email': d.email
          };
          var deliveryTags = (d.tags && d.tags.length > 0) ? d.tags : (tagsByStopId[d.stop_id] || []);
          if (this.includeTagData && deliveryTags.length > 0) {
            deliveryTags.forEach(function(tag) {
              var row = Object.assign({}, baseRow);
              row['tag_climate'] = climateMap[tag.climate_id] || tag.climate_id || '';
              row['tag_consolidation_depot'] = depotMap[tag.consolidation_depot_id] || tag.consolidation_depot_id || '';
              row['tag_created'] = tag.created;
              row['tag_description'] = tag.description;
              row['tag_id'] = tag.id;
              row['tag_item'] = tag.item;
              row['tag_linehaul_id'] = tag.linehaul_id;
              row['tag_modified'] = tag.modified;
              row['tag_number'] = tag.number;
              row['tag_production_code'] = tag.production_code;
              row['tag_production_date'] = tag.production_date;
              row['tag_production_shift'] = tag.production_shift;
              row['tag_quantity'] = tag.quantity;
              row['tag_stop'] = tag.stop;
              row['tag_transaction'] = tag.transaction;
              row['tag_type'] = tag.type;
              mappedData.push(row);
            });
          } else {
            var row = Object.assign({}, baseRow);
            if (this.includeTagData) {
              row['tag_climate'] = '';
              row['tag_consolidation_depot'] = '';
              row['tag_created'] = '';
              row['tag_description'] = '';
              row['tag_id'] = '';
              row['tag_item'] = '';
              row['tag_linehaul_id'] = '';
              row['tag_modified'] = '';
              row['tag_number'] = '';
              row['tag_production_code'] = '';
              row['tag_production_date'] = '';
              row['tag_production_shift'] = '';
              row['tag_quantity'] = '';
              row['tag_stop'] = '';
              row['tag_transaction'] = '';
              row['tag_type'] = '';
            }
            mappedData.push(row);
          }
        }.bind(this));
        this.exportDeliveries = mappedData;
        // --- CSV Generation and Download ---
        if (exportError || apiError) {
          let msg = 'Export failed: ' + (exportError && exportError.message ? exportError.message : apiError || 'API error');
          this.$store && this.$store.commit && this.$store.commit('displaySnackbar', msg);
          if (window && window.console) {
            console.error('Export failed', exportError || apiError);
          }
          return [];
        }
        if (!mappedData.length) {
          this.$store && this.$store.commit && this.$store.commit('displaySnackbar', 'No data found for export.');
          if (window && window.console) {
            console.warn('No data found for export.');
          }
          return [];
        }
        const fields = Object.keys(mappedData[0]);
        const csv = Papa.unparse({ fields: fields, data: mappedData });
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const blobUrl = URL.createObjectURL(blob);
        link.setAttribute('href', blobUrl);
        link.setAttribute('download', 'deliveries.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        this.$store && this.$store.commit && this.$store.commit('displaySnackbar', 'Export successful!');
        // --- End CSV Generation ---
        return mappedData;
      } catch (err) {
        if (window && window.console) {
          console.error('Export failed', err);
        }
        this.$store && this.$store.commit && this.$store.commit('displaySnackbar', 'Export failed: ' + (err.message || 'Unknown error'));
        return [];
      } finally {
        if (this.exportTimerInterval) {
          clearInterval(this.exportTimerInterval);
          this.exportTimerInterval = null;
        }
        if (!this.isExportCancelled) {
          this.exportLoading = false;
        }
      }
    },

    // --- Top Scrollbar Sync Logic ---
    onTopScroll: function(e) {
      if (this.isSyncingLeft) return;
      this.isSyncingLeft = true;
      if (this.tableWrapperElement) {
        this.tableWrapperElement.scrollLeft = e.target.scrollLeft;
      }
      this.isSyncingLeft = false;
    },
    onTableScroll: function(e) {
      if (this.isSyncingLeft) return;
      this.isSyncingLeft = true;
      if (this.$refs.topScrollWrapper) {
        this.$refs.topScrollWrapper.scrollLeft = e.target.scrollLeft;
      }
      this.isSyncingLeft = false;
    },
    updateScrollbarWidth: function() {
      // Find the inner table
      if (this.$refs.table && this.$refs.table.$el) {
        var table = this.$refs.table.$el.querySelector('table');
        var wrapper = this.$refs.table.$el.querySelector('.v-data-table__wrapper');

        if (table && wrapper) {
          // Set the top scrollbar's inner div width to match the real table's width
          this.tableScrollWidth = table.scrollWidth;

          // Store reference for sync events if not already done
          if (!this.tableWrapperElement) {
            this.tableWrapperElement = wrapper;
            wrapper.addEventListener('scroll', this.onTableScroll);
          }
        }
      }
    }
  },
    watch: {
    clientSearch: function(val) {
      if (!val || val.length >= 2) this.debouncedFetchClients(val || '');
    },
    depotSearch: function(val) {
      if (!val || val.length >= 2) this.debouncedFetchDepots(val || '');
    },
    consolidationDepotSearch: function(val) {
      if (!val || val.length >= 2) this.debouncedFetchDepots(val || '');
    },
    regionSearch: function(val) {
      if (!val || val.length >= 2) this.debouncedFetchRegions(val || '');
    },
    'filters.country': function() {
      if (!this._autoSelectingCountry) {
        this.filters.region = this.filters.depot = this.filters.consolidationDepot = null;
        this.filters.client = [];
      }
      this.loadClients('');
      this.loadRegions('');
    },
    'filters.region': function(val) { this.filters.depot = null; this.autoSelectCountryFor('region', val); },
    'filters.depot': function(val) { this.autoSelectCountryFor('depot', val); },
    'filters.client': { handler: function(val) { this.autoSelectCountryFor('client', val); }, deep: true },
    'filters.consolidationDepot': function(val) { this.autoSelectCountryFor('consolidationDepot', val); },
    // Watch tracking, invoice, and serial to clear date filters in UI
    'filters.tracking': function(val) { this.clearDatesIfSpecialFilters(); },
    'filters.invoice': function(val) { this.clearDatesIfSpecialFilters(); },
    'filters.serial': function(val) { this.clearDatesIfSpecialFilters(); }
    },
  created: function() {
    this.debouncedLoadDeliveries = debounce(this.loadDeliveries.bind(this), 500);
    this.debouncedFetchClients = debounce(this.loadClients.bind(this), 350);
    this.debouncedFetchDepots = debounce(this.loadDepots.bind(this), 350);
    this.debouncedFetchRegions = debounce(this.loadRegions.bind(this), 350);
  },
  beforeDestroy: function() {
    [this.debouncedLoadDeliveries, this.debouncedFetchClients, this.debouncedFetchDepots, this.debouncedFetchRegions].forEach(function(d) {
      if (d && d.cancel) d.cancel();
    });

    // Clear export timer
    if (this.exportTimerInterval) {
      clearInterval(this.exportTimerInterval);
      this.exportTimerInterval = null;
    }

    // Remove scroll listener
    if (this.tableWrapperElement) {
      this.tableWrapperElement.removeEventListener('scroll', this.onTableScroll);
    }
  },
  mounted: function() {
    // Set default date to today (query from current delivery date onwards)
    this.filters.dateFrom = this.getTodayDate();

    // For external users, auto-select their client's country to scope all dependent filters
    if (!this.isInternalUser) {
      var client = this.$store.state.auth.client;
      if (client && client.default_country) {
        this.filters.country = client.default_country;
      }
    }

    this.loadClients('');
    this.loadDepots('');
    this.loadRegions('');
    this.loadServices();
    this.loadClimates();
    // Initial load: mark as applied so UI doesn't show pending at start
    this.loadDeliveries(this.filters, this.tableOptions, true);
  }
}
</script>

<style scoped>
.border-subtle { border: 1px solid #E0E0E0 !important; }
.border-top-subtle { border-top: 1px solid #E0E0E0 !important; }
.border-bottom-subtle { border-bottom: 1px solid #E0E0E0 !important; }

/* Filter group container for equal height columns */
.filter-group-wrapper {
  background-color: #FAFAFA;
  border-radius: 6px;
  padding: 12px;
  border: 1px solid #F0F0F0;
  height: 100%;
}

.slim-input >>> .v-input__control { min-height: 36px !important; }
.slim-input >>> .v-label { font-size: 0.8125rem !important; top: 8px !important; }
.slim-input >>> .v-input__slot { min-height: 36px !important; padding: 0 8px !important; }
.slim-input >>> .v-text-field__details { display: none; }
.slim-input >>> .v-input__append-inner { margin-top: 6px !important; }
.slim-input >>> .v-input__prepend-inner { margin-top: 6px !important; }

.slim-input >>> .v-select__selections { min-height: 28px !important; padding: 2px 0 !important; flex-wrap: wrap; gap: 2px; }
.slim-input >>> .v-select__selections .v-chip { margin: 1px 2px !important; height: 20px !important; font-size: 0.7rem !important; }
.slim-input >>> .v-select__selections .v-chip .v-chip__close { font-size: 14px !important; margin-left: 2px !important; }

.slim-checkbox >>> .v-label { font-size: 0.8125rem !important; color: #546E7A; }
.slim-checkbox >>> .v-icon { font-size: 18px !important; }

.export-group { border: 1px solid #CFD8DC; background-color: #FAFAFA; }

.labels-select { max-width: 130px; }

.loading-disabled { pointer-events: none; opacity: 0.7; }

.filter-category-tag { display: flex; align-items: center; }

.minimize-panel-btn-outer { margin-top: 4px !important; }

.date-picker-field >>> .v-input__control { cursor: pointer !important; }

.created-filter-group { border: 1px solid #E0E0E0; background-color: #FAFAFA; }

/* Horizontal Scrollbar Styling */
.top-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid #E0E0E0;
}

.horizontal-scroll-table >>> .v-data-table__wrapper { overflow-x: auto !important; }
.horizontal-scroll-table >>> table { min-width: 1700px; table-layout: fixed; }

.slim-table >>> th {
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #546E7A !important;
  background-color: #F5F7F8 !important;
  height: 42px !important;
  border-bottom: 1px solid #CFD8DC !important;
  white-space: nowrap;
}

.slim-table >>> td {
  font-size: 0.8125rem !important;
  height: 44px !important;
  border-bottom: 1px solid #EEEEEE !important;
  color: #37474F;
}

.slim-table >>> tbody tr:hover { background-color: #F5F5F5 !important; }

.cursor-pointer { cursor: pointer; }
.hover-primary:hover { color: #1976D2 !important; background-color: rgba(25, 118, 210, 0.1); }

/* Deleted delivery: fill the entire Actions cell red with white label.
   Negative margins cancel the td's 16px horizontal / default vertical padding
   so the red fills edge-to-edge. */
.deleted-cell {
  background-color: #C62828;
  height: 44px;
  margin: 0 -16px;
  letter-spacing: 0.05em;
}

.slim-footer {
  border: none !important;
  margin: 0 !important;
  background: transparent !important;
  padding: 0 !important;
  height: auto !important;
  min-height: 32px !important;
}

.slim-footer >>> .v-data-footer__select,
.slim-footer >>> .v-data-footer__pagination {
  font-size: 0.8125rem !important;
  margin: 0 8px !important;
  color: #78909C;
}

.slim-footer >>> .v-btn {
  padding: 0 !important;
  width: 28px !important;
  height: 28px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Delivery/DeliveryListing.vue